import { useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";

import { useTRPC } from "@/trpc/client";

const INTERVAL_MS = 5 * 60 * 1000;
/**
 * Floor on how often a visibility-regain can trigger a write, independent of
 * the 5-minute interval. Without this, rapidly switching tabs/windows back to
 * Graftus (e.g. alt-tabbing every few seconds) fired one write per regain with
 * no lower bound - see GRA-262.
 */
const MIN_VISIBILITY_BEAT_GAP_MS = 60 * 1000;

/**
 * Feeds the Data page: one heartbeat on sign-in (the login stamp) and one
 * every five minutes while the tab is visible, carrying the minutes since the
 * previous beat. Failures are swallowed: activity is a by-product, never a
 * blocker.
 */
export function useActivityHeartbeat(businessId: string | undefined) {
  const trpc = useTRPC();
  const { mutate } = useMutation(trpc.intelligence.activity.heartbeat.mutationOptions());
  const lastBeat = useRef<number | null>(null);

  useEffect(() => {
    if (!businessId) return;
    const beat = (isLogin: boolean) => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      const minutes = lastBeat.current
        ? Math.min(15, Math.round((now - lastBeat.current) / 60000))
        : 0;
      lastBeat.current = now;
      mutate({ businessId, minutes, isLogin }, { onError: () => undefined });
    };
    beat(true);
    const timer = window.setInterval(() => beat(false), INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // The interval timer already beats every 5 minutes; this floor only
      // guards the uncapped path - a visibility regain right after another
      // beat (interval- or regain-triggered) is a no-op instead of a write.
      if (lastBeat.current && Date.now() - lastBeat.current < MIN_VISIBILITY_BEAT_GAP_MS) {
        return;
      }
      beat(false);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [businessId, mutate]);
}
